// Column width math for Table. Pure functions: header measurements come in as column specs
// from useColumnLayout, pixel widths go out.
//
// A column spec is `{ weight, fixed, floor, comfort, tight, scrollFloor? }`:
// - `weight`: share of the flexible space (null = equal share of whatever percentages leave).
// - `fixed`: pixel-width (or control) column; it never takes a share of the flexible space.
// - `floor`: narrowest width (title wrapped to its longest word, or the column's `minWidth`).
// - `comfort`: width with the title on one line (or the column's pixel width).
// - `tight`: stays at its floor and gets no slack.
// - `scrollFloor`: width once the table scrolls anyway (defaults to `floor`). Floors only exist to
//   avoid scrolling; past that point a column can keep a more readable width at no extra cost.

// Share of the gap to the widest flexible title that shorter columns keep as spare room.
const HEADER_SLACK_BLEND = 0.5

const withSlack = (columns) => {
  const widest = columns.reduce((max, column) => (column.tight ? max : Math.max(max, column.comfort)), 0)
  return columns.map((column) => ({
    ...column,
    slack: column.tight ? column.comfort : column.comfort + Math.max(0, widest - column.comfort) * HEADER_SLACK_BLEND,
  }))
}

// Level 0 = every floor, 1 = every comfort width, 2 = every slack width.
const minAtLevel = (column, level) =>
  level <= 1
    ? column.floor + (column.comfort - column.floor) * level
    : column.comfort + (column.slack - column.comfort) * (level - 1)

const sumAtLevel = (columns, level) => columns.reduce((sum, column) => sum + minAtLevel(column, level), 0)

// Highest level whose minimums still fit. Titles wrap before the table scrolls sideways.
const fitLevel = (available, columns) => {
  if (sumAtLevel(columns, 2) <= available) {
    return 2
  }
  if (sumAtLevel(columns, 0) >= available) {
    return 0
  }
  let low = 0
  let high = 2
  for (let step = 0; step < 20; step += 1) {
    const mid = (low + high) / 2
    if (sumAtLevel(columns, mid) <= available) {
      low = mid
    } else {
      high = mid
    }
  }
  return low
}

// Percentage (or equal) shares hold while columns still have room above their minimum.
// Scrolling starts once every column is down to its floor; from then on each column takes its
// scroll floor.
const distributeColumnWidths = (available, columns) => {
  const scrolls = sumAtLevel(columns, 0) > available
  const level = fitLevel(available, columns)
  const mins = columns.map((column) => (scrolls ? (column.scrollFloor ?? column.floor) : minAtLevel(column, level)))
  const widths = columns.map((column, index) => (column.fixed ? mins[index] : 0))
  const flexibleIndexes = columns.flatMap((column, index) => (column.fixed ? [] : [index]))
  const minSum = mins.reduce((sum, min) => sum + min, 0)

  if (available <= minSum) {
    flexibleIndexes.forEach((index) => {
      widths[index] = mins[index]
    })
    return { widths, minSum }
  }

  const assignFlexibleWidths = (space, active) => {
    if (!active.length) {
      return
    }

    const weightSum = active.reduce((sum, index) => sum + columns[index].weight, 0)
    const pending = []
    let nextSpace = space
    let clamped = false

    active.forEach((index) => {
      const share = (space * columns[index].weight) / weightSum
      if (share < mins[index]) {
        widths[index] = mins[index]
        nextSpace -= mins[index]
        clamped = true
      } else {
        pending.push(index)
      }
    })

    if (!clamped) {
      active.forEach((index) => {
        widths[index] = (space * columns[index].weight) / weightSum
      })
      return
    }

    assignFlexibleWidths(nextSpace, pending)
  }

  const fixedSum = columns.reduce((sum, column, index) => sum + (column.fixed ? mins[index] : 0), 0)
  assignFlexibleWidths(available - fixedSum, flexibleIndexes)

  return { widths, minSum }
}

// Columns without a percentage split what the percentage columns leave, equally.
const withWeights = (specs) => {
  const flexCount = specs.filter((column) => column.weight == null).length
  const percentTotal = specs.reduce((sum, column) => sum + (column.weight || 0), 0)
  const flexWeight = flexCount > 0 ? Math.max(0, 1 - percentTotal) / flexCount : 0
  return specs.map((column) => ({
    ...column,
    weight: column.weight == null ? flexWeight : column.weight,
  }))
}

// Pixel width for each column in `available` space, plus the table's minimum width
// (anything narrower than `minSum` scrolls sideways).
const computeColumnWidths = (available, specs) => distributeColumnWidths(available, withSlack(withWeights(specs)))

export default computeColumnWidths
